import React, { useState, useCallback, useRef, useEffect, useImperativeHandle } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  useTheme,
  FormControlLabel,
  Switch,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Info,
  Delete,
  Visibility
} from '@mui/icons-material';
import { useImportExport } from '../context';
import { ImportSectionProps } from '../types/ComponentTypes';
import FileUploadMAIQ from '../../FileUploadMAIQ';
import ComparePreview, { ComparePreviewRef } from './ComparePreview';
import ImportStepIndicator from './ImportStepIndicator';
import JsonDiffViewer from './JsonDiffViewer';
import ImportExportApiService from '../apiService/importExportApiService';
import { validateFileSizeWithUncompressedEstimate } from '../utils/validationUtils';
import { createFileSizeError, formatErrorForUI, logFileSizeError } from '../utils/errorUtils';

const ImportSection = React.forwardRef<any, ImportSectionProps>(({
  config,
  onImport,
  onError,
  loading = false,
  onImportEditedData
}, ref) => {
  const theme = useTheme();
  const { 
    handleImport, 
    importLoading, 
    importError, 
    importData, 
    validationResult,
    importFile,
    setImportFile,
    handleCompareZip,
    compareData,
    compareLoading,
    compareError,
    handleImportCompareData,
    resetState,
    setError,
    setImportError,
    setLoading,
    // Entity selection
    availableEntities,
    selectedEntity,
    entityFilters,
    entitiesLoading,
    entitiesError,
    loadEntities,
    selectEntity
  } = useImportExport();
  
  const [validateOnly, setValidateOnly] = useState(true);
  const [updateExisting, setUpdateExisting] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileUploadKey, setFileUploadKey] = useState(0); // Key to force FileUploadMAIQ remount
  
  // Step management state
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  
  // Confirmation modal state
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [pendingImportData, setPendingImportData] = useState<any[]>([]);
  
  // Ref to access ComparePreview's edited data
  const comparePreviewRef = useRef<ComparePreviewRef>(null);
  
  // State to preserve edited data when navigating between steps
  const [preservedEditedData, setPreservedEditedData] = useState<any[]>([]);
  const [hasEditedData, setHasEditedData] = useState(false);
  
  // State to preserve original data for diff display
  const [originalCompareData, setOriginalCompareData] = useState<any[]>([]);

  // Load entities on component mount
  useEffect(() => {
    loadEntities();
  }, [loadEntities]);

  // Store original compare data when it first arrives - deep clone to prevent mutations
  useEffect(() => {
    if (compareData && compareData.length > 0 && originalCompareData.length === 0) {
      // Deep clone the data to prevent any mutations
      const deepClonedData = JSON.parse(JSON.stringify(compareData));
      setOriginalCompareData(deepClonedData);
    }
  }, [compareData, originalCompareData.length]);

  // Reset local states function
  const resetLocalStates = useCallback(() => {
    setValidateOnly(true);
    setUpdateExisting(true);
    setUploadProgress(0);
    setFileUploadKey(prev => prev + 1); // Force FileUploadMAIQ to remount
    setConfirmationModalOpen(false);
    setPendingImportData([]);
    setCurrentStep(0);
    setCompletedSteps([]);
    setPreservedEditedData([]);
    setHasEditedData(false);
    setOriginalCompareData([]);
  }, []);

  // Complete reset function that resets both local and context states
  const resetAllStates = useCallback(() => {
    resetLocalStates();
    resetState();
    // Also reset ComparePreview if it exists
    if (comparePreviewRef.current?.resetEditingStates) {
      comparePreviewRef.current.resetEditingStates();
    }
  }, [resetLocalStates, resetState]);

  // Step management functions
  const goToStep = useCallback((step: number) => {
    setCurrentStep(step);
  }, []);

  const markStepCompleted = useCallback((step: number) => {
    setCompletedSteps(prev => {
      if (!prev.includes(step)) {
        return [...prev, step];
      }
      return prev;
    });
  }, []);

  const nextStep = useCallback(() => {
    // Save current edited data before moving to next step
    if (comparePreviewRef.current && currentStep === 1) {
      const editedData = comparePreviewRef.current.getEditedData();
      if (editedData && editedData.length > 0) {
        setPreservedEditedData(editedData);
        setHasEditedData(true);
      }
    }
    markStepCompleted(currentStep);
    setCurrentStep(prev => Math.min(prev + 1, 2));
  }, [currentStep, markStepCompleted]);

  const prevStep = useCallback(() => {
    // Save current edited data before going back
    if (comparePreviewRef.current && currentStep === 1) {
      const editedData = comparePreviewRef.current.getEditedData();
      if (editedData && editedData.length > 0) {
        setPreservedEditedData(editedData);
        setHasEditedData(true);
      }
    }
    setCurrentStep(prev => Math.max(prev - 1, 0));
  }, [currentStep]);

  // Function to save edited data
  const saveEditedData = useCallback(() => {
    if (comparePreviewRef.current) {
      const editedData = comparePreviewRef.current.getEditedData();
      if (editedData && editedData.length > 0) {
        setPreservedEditedData(editedData);
        setHasEditedData(true);
      }
    }
  }, []);

  // Function to restore edited data
  const restoreEditedData = useCallback(() => {
    if (hasEditedData && preservedEditedData.length > 0 && comparePreviewRef.current) {
      // Reset the ComparePreview to the preserved data
      comparePreviewRef.current.resetEditingStates();
      // The ComparePreview will use the preserved data
    }
  }, [hasEditedData, preservedEditedData]);

  // Import edited data function (moved up for dependency order)
  const handleImportEditedData = useCallback(async (editedData: any[]) => {
    try {
      // Use the stored original compare data (deep cloned to prevent mutations)
      const originalData = originalCompareData;
      
      
      // Filter out entities that are identical to current data
      const differentEntities = editedData.filter(item => {
        if (!item.current || !item.incoming) {
          return true; // Keep if no current data (new entity)
        }
        
        // Compare current vs incoming data
        return JSON.stringify(item.current) !== JSON.stringify(item.incoming);
      });
      
      // Create diff data for confirmation step - show original current vs final incoming
      const diffData = differentEntities.map(item => {
        // Find the original data from the original compare data
        // Use a more robust matching - try to match by ID or other unique identifier first
        let originalItem = originalData.find(orig => {
          // First try to match by ID if available
          if (orig.current?._id && item.current?._id) {
            return orig.current._id === item.current._id;
          }
          // Fallback to full object comparison
          return orig.current && item.current && 
            JSON.stringify(orig.current) === JSON.stringify(item.current);
        });
        
        // If still not found, try to match by incoming data ID
        if (!originalItem && item.incoming?._id) {
          originalItem = originalData.find(orig => 
            orig.incoming?._id === item.incoming._id
          );
        }
        
        
        
        return {
          current: originalItem ? originalItem.current : item.current, // Original current data (unchanged)
          incoming: item.incoming, // Final incoming data (after any edits)
          originalIncoming: originalItem ? originalItem.incoming : item.incoming // Original incoming data for reference
        };
      });
      
      // Set pending import data with diff information
      setPendingImportData(diffData);
      nextStep(); // Move to step 3 (Confirm)
    } catch (error) {
      onError(error);
      throw error;
    }
  }, [config.entityType, config.dealerId, onImport, onError, originalCompareData, nextStep]);

  // Function to perform the actual import
  const performImport = useCallback(async () => {
    try {
      setLoading(true);
      
      // Extract incoming data from pending import data
      const incomingData = pendingImportData
        .filter(item => item.incoming) // Only items with incoming data
        .map(item => item.incoming); // Extract the incoming data
      
      if (incomingData.length === 0) {
        throw new Error('No data to import');
      }
      
      // Import the incoming data directly using the API service
      // Use selected entity if available, otherwise fallback to config.entityType
      const entityType = selectedEntity?.entity || config.entityType;
      const response = await ImportExportApiService.importDataFromJson(entityType, incomingData, {
        dealer: config.dealerId,
        validate_only: false,
        update_existing: true
      });
      
      if (response.body) {
        // Close confirmation modal
        setConfirmationModalOpen(false);
        
        // Call success callback
        const importResult = {
          type: 'import' as const,
          success: response.body.success,
          importedCount: response.body.imported || 0,
          updatedCount: response.body.updated || 0,
          failedCount: response.body.failed || 0,
          errors: response.body.errors || [],
          warnings: response.body.warnings || [],
          downloadUrl: undefined
        };
        
        console.log('Calling onImport with result:', importResult);
        onImport(importResult);
      } else {
        throw new Error(response.message || 'Failed to import data');
      }
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  }, [pendingImportData, config.entityType, config.dealerId, onImport, onError, setLoading]);

  // Confirmation modal handlers
  const handleConfirmImport = useCallback(async () => {
    setConfirmationModalOpen(false);
    setLoading(true);
    
    try {
      // Check if we have compare data (ZIP files) or regular import data
      if (compareData && compareData.length > 0) {
        // For ZIP files with compare data - use handleImportEditedData
        await handleImportEditedData(pendingImportData);
      } else {
        // For regular files - use handleImport
        if (importFile) {
          await handleImport(importFile, false);
          
          onImport({
            success: true,
            importedCount: importData?.length || 0,
            failedCount: 0,
            errors: validationResult?.errors || [],
            warnings: validationResult?.warnings || [],
            downloadUrl: undefined
          });
        }
      }
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  }, [pendingImportData, onError, setLoading, compareData, handleImportEditedData, handleImport, importFile, importData, validationResult, onImport, selectedEntity]);

  const handleCancelImport = useCallback(() => {
    setConfirmationModalOpen(false);
    setPendingImportData([]);
  }, []);

  // Expose functions through ref
  useImperativeHandle(ref, () => ({
    resetAllStates,
    resetLocalStates,
    getEditedData: () => comparePreviewRef.current?.getEditedData?.() || []
  }), [resetAllStates, resetLocalStates]);



  const handleFileSelect = useCallback(async (file: File | null) => {
    // Handle file deletion (null file)
    if (!file) {
      // Reset all local states
      resetLocalStates();
      
      // Reset all context states
      resetState();
      
      // Also reset ComparePreview if it exists
      if (comparePreviewRef.current?.resetEditingStates) {
        comparePreviewRef.current.resetEditingStates();
      }
      
      return;
    }
    
    try {
      setUploadProgress(0);
      
      // Clear any previous errors
      setImportError(null);
      
      // Check if entity is selected
      if (!selectedEntity) {
        throw new Error('Please select an entity type first');
      }
      
      // Validate file size for memory management BEFORE processing
      const sizeValidation = validateFileSizeWithUncompressedEstimate(file);
      
      if (!sizeValidation.isValid) {
        // Create a detailed error with suggestions
        const isZipFile = file.name.toLowerCase().endsWith('.zip');
        const fileSizeError = createFileSizeError(
          file, 
          isZipFile ? 512 * 1024 : 1024 * 1024,
          isZipFile ? 'compressed_too_large' : 'file_too_large'
        );
        
        // Log the error for debugging
        logFileSizeError(fileSizeError, 'ImportSection.handleFileSelect');
        
        // Format error for UI display with better formatting
        const errorInfo = formatErrorForUI(fileSizeError);
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        const maxSizeMB = isZipFile ? '0.5' : '1.0';
        
        // Create a professional error message
        let detailedMessage = `File size (${fileSizeMB}MB) exceeds the maximum allowed size of ${maxSizeMB}MB. Please select a smaller file.`;
        
        throw new Error(detailedMessage);
      }
      
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);
      
      // Check if it's a ZIP file and call compare API
      if (file.name.toLowerCase().endsWith('.zip')) {
        await handleCompareZip(file);
        // Set importFile for ZIP files too so UI shows selected file
        setImportFile(file);
        // Move to step 2 (Edit Data) after successful comparison
        nextStep();
      } else {
        await handleImport(file, validateOnly);
      }
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Call success callback
      onImport({
        success: true,
        importedCount: importData?.length || 0,
        failedCount: 0,
        errors: validationResult?.errors || [],
        warnings: validationResult?.warnings || [],
        downloadUrl: undefined
      });
      
    } catch (error) {
      setUploadProgress(0);
      
      // Set the error state directly to ensure it shows in UI
      if (error instanceof Error) {
        setImportError(error.message);
      }
      
      onError(error);
    }
  }, [handleImport, handleCompareZip, validateOnly, importData, validationResult, onImport, onError, resetState, selectedEntity]);

  const handleImportClick = async () => {
    if (!importFile) return;
    
    // Always show confirmation modal first
    if (compareData && compareData.length > 0) {
      // For ZIP files with compare data
      const editedData = comparePreviewRef.current?.getEditedData?.() || compareData;
      
      // Filter out entities that are identical to current data
      const differentEntities = editedData.filter(item => {
        if (!item.current || !item.incoming) {
          return true; // Keep if no current data (new entity)
        }
        
        // Compare current vs incoming data
        return JSON.stringify(item.current) !== JSON.stringify(item.incoming);
      });
      
      if (differentEntities.length === 0) {
        // No different entities to import
        onImport({
          success: true,
          importedCount: 0,
          failedCount: 0,
          errors: [],
          warnings: [],
          downloadUrl: undefined
        });
        return;
      }
      
      // Set pending import data and move to confirm step
      setPendingImportData(differentEntities);
      nextStep(); // Move to step 3 (Confirm)
    } else {
      // For regular files (CSV, JSON) without compare data
      // Create a simple data structure for confirmation
      const simpleImportData = importData ? importData.map((item, index) => ({
        current: null, // No existing data for comparison
        incoming: item // The data to be imported
      })) : [];
      
      if (simpleImportData.length === 0) {
        // No data to import
        onImport({
          success: true,
          importedCount: 0,
          failedCount: 0,
          errors: [],
          warnings: [],
          downloadUrl: undefined
        });
        return;
      }
      
      // Set pending import data and move to confirm step
      setPendingImportData(simpleImportData);
      nextStep(); // Move to step 3 (Confirm)
    }
  };


  // Function to import edited data from ComparePreview ref
  const importEditedDataFromRef = async () => {
    if (!comparePreviewRef.current) {
      return;
    }
    
    const editedData = comparePreviewRef.current.getEditedData();
    await handleImportEditedData(editedData);
  };

  const getSupportedFileTypes = () => {
    return config.supportedFormats.map(format => `.${format}`).join(', ');
  };

  const renderValidationResults = () => {
    if (!validationResult) return null;

    const { errors, warnings } = validationResult;
    const errorCount = errors?.length || 0;
    const warningCount = warnings?.length || 0;
    const isValid = errorCount === 0;

    return (
      <Card elevation={1} sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Validation Results
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Chip
              icon={isValid ? <CheckCircle /> : <ErrorIcon />}
              label={`${isValid ? 'Valid' : 'Invalid'} Data`}
              color={isValid ? 'success' : 'error'}
              variant="outlined"
            />
            {errorCount > 0 && (
              <Chip
                icon={<ErrorIcon />}
                label={`${errorCount} Errors`}
                color="error"
                variant="outlined"
              />
            )}
            {warningCount > 0 && (
              <Chip
                icon={<Warning />}
                label={`${warningCount} Warnings`}
                color="warning"
                variant="outlined"
              />
            )}
          </Box>

          {errors && errors.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="error" gutterBottom>
                Errors:
              </Typography>
              <List dense>
                {errors.slice(0, 5).map((error: any, index: number) => (
                  <ListItem key={index} sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <ErrorIcon color="error" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={error.message}
                      secondary={error.field ? `Field: ${error.field}` : ''}
                    />
                  </ListItem>
                ))}
                {errors.length > 5 && (
                  <ListItem sx={{ py: 0 }}>
                    <ListItemText
                      primary={`... and ${errors.length - 5} more errors`}
                      sx={{ fontStyle: 'italic' }}
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          )}

          {warnings && warnings.length > 0 && (
            <Box>
              <Typography variant="subtitle2" color="warning.main" gutterBottom>
                Warnings:
              </Typography>
              <List dense>
                {warnings.slice(0, 3).map((warning: any, index: number) => (
                  <ListItem key={index} sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Warning color="warning" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={warning.message}
                      secondary={warning.field ? `Field: ${warning.field}` : ''}
                    />
                  </ListItem>
                ))}
                {warnings.length > 3 && (
                  <ListItem sx={{ py: 0 }}>
                    <ListItemText
                      primary={`... and ${warnings.length - 3} more warnings`}
                      sx={{ fontStyle: 'italic' }}
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ 
      p: 2.5,
      paddingBottom: '100px', // Add padding to prevent content from being hidden behind fixed buttons
      '@keyframes pulse': {
        '0%': {
          boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
        },
        '50%': {
          boxShadow: '0 6px 20px rgba(211, 47, 47, 0.5)',
        },
        '100%': {
          boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
        },
      }
    }}>
      {/* <Typography 
        variant="h6" 
        gutterBottom 
        sx={{ 
          color: '#0F2A52',
          fontWeight: 600,
          fontSize: '1.1rem',
          mb: 1
        }}
      >
        Import {config.entityType.charAt(0).toUpperCase() + config.entityType.slice(1)} Data
      </Typography> */}
      
      {/* <Typography 
        variant="body2" 
        color="text.secondary" 
        sx={{ 
          mb: 3,
          fontSize: '0.85rem',
          lineHeight: 1.5
        }}
      >
        Select a file to import data. Supported formats: {getSupportedFileTypes()}
        <br />
        <Typography variant="caption" sx={{ 
          color: '#6c757d', 
          fontSize: '0.75rem',
          fontStyle: 'italic'
        }}>
          Max file size: 1MB (uncompressed) / 512KB (compressed)
        </Typography>
      </Typography> */}

      {/* Step Indicator */}
      <ImportStepIndicator 
        activeStep={currentStep} 
        completedSteps={completedSteps} 
      />

      {importError && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2,
            borderRadius: 1,
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 400, color: '#721c24' }}>
            {importError}
          </Typography>
        </Alert>
      )}

      {/* Step 1: Select Entity and File */}
      {currentStep === 0 && (
        <Grid container spacing={3}>
          {/* Entity Selection */}
          <Grid item xs={12}>
            <Card 
              elevation={0}
              sx={{
                borderRadius: 1.5,
                boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
                border: '1px solid rgba(15, 40, 76, 0.08)',
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{
                    color: '#0F2A52',
                    fontWeight: 600,
                    fontSize: '1rem',
                    mb: 2
                  }}
                >
                  Select Entity Type *
                </Typography>
                
                {entitiesError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {entitiesError}
                  </Alert>
                )}
                
                <FormControl fullWidth size="small" required>
                  <InputLabel>Choose Entity *</InputLabel>
                  <Select
                    value={selectedEntity?.entity || ''}
                    label="Choose Entity *"
                    onChange={(e) => {
                      const entity = availableEntities.find(ent => ent.entity === e.target.value);
                      if (entity) {
                        selectEntity(entity);
                      }
                    }}
                    disabled={entitiesLoading}
                    required
                  >
                    {entitiesLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Loading entities...
                      </MenuItem>
                    ) : (
                      availableEntities.map((entity) => (
                        <MenuItem key={entity.entity} value={entity.entity}>
                          {entity.entity.charAt(0).toUpperCase() + entity.entity.slice(1)}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                
                {selectedEntity && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Selected: {selectedEntity.entity} 
                    {entityFilters.length > 0 && ` (${entityFilters.length} filters available)`}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* File Upload */}
          <Grid item xs={12}>
            <Card 
              elevation={0}
              sx={{
                borderRadius: 1.5,
                boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
                border: '1px solid rgba(15, 40, 76, 0.08)',
                opacity: !selectedEntity ? 0.6 : 1,
                transition: 'opacity 0.2s ease',
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{
                    color: '#0F2A52',
                    fontWeight: 600,
                    fontSize: '1rem',
                    mb: 2
                  }}
                >
                  Upload File
                </Typography>
                
                <FileUploadMAIQ
                  key={fileUploadKey}
                  selectedFile={importFile}
                  onFileSelect={(file) => handleFileSelect(file)}
                  accept={getSupportedFileTypes()}
                  disabled={importLoading || loading || !selectedEntity}
                />
                
                {!selectedEntity && (
                  <Box sx={{ 
                    mt: 2, 
                    p: 2, 
                    backgroundColor: 'rgba(255, 193, 7, 0.1)', 
                    borderRadius: 1,
                    border: '1px solid rgba(255, 193, 7, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <Warning sx={{ color: '#ff9800', fontSize: 20 }} />
                    <Typography 
                      variant="body2"
                      sx={{
                        color: '#e65100',
                        fontWeight: 500
                      }}
                    >
                      Please select an entity type above to enable file upload
                    </Typography>
                  </Box>
                )}
                
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Validating file...
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={uploadProgress} 
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                )}
                
                {importFile && (
                  <Box sx={{ 
                    mt: 2, 
                    p: 2, 
                    backgroundColor: 'rgba(15, 42, 82, 0.04)', 
                    borderRadius: 1,
                    border: '1px solid rgba(15, 42, 82, 0.08)',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Info sx={{ color: '#0F2A52' }} />
                        <Typography 
                          variant="body2"
                          sx={{
                            color: '#0F2A52',
                            fontWeight: 500
                          }}
                        >
                          Selected: {importFile.name} ({(importFile.size / 1024 / 1024).toFixed(2)} MB)
                        </Typography>
                      </Box>
                      <IconButton 
                        size="small" 
                        onClick={() => handleFileSelect(null)}
                        sx={{
                          color: '#d32f2f',
                          '&:hover': {
                            backgroundColor: 'rgba(211, 47, 47, 0.04)',
                          },
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Validation Results */}
          {validationResult && renderValidationResults()}
        </Grid>
      )}

      {/* Step 2: Edit Data */}
      {currentStep === 1 && (
        <Grid container spacing={3}>
          {/* Compare Results for ZIP files */}
          {importFile && importFile.name.toLowerCase().endsWith('.zip') && (
            <Grid item xs={12}>
              <ComparePreview
                ref={comparePreviewRef}
                compareData={compareData}
                loading={compareLoading}
                error={compareError}
                preservedData={hasEditedData ? preservedEditedData : null}
                onAcceptChanges={async (editedData) => {
                  await handleImportEditedData(editedData);
                }}
                onRejectChanges={() => {
                  // Future implementation
                }}
                onDataChange={(index, field, value) => {
                  // Future implementation for saving changes
                }}
              />
            </Grid>
          )}

          {/* Fixed Bottom Navigation Buttons for Step 2 */}
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: 'white',
              borderTop: '1px solid #e0e0e0',
              p: 2,
              zIndex: 1000,
              boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Box sx={{ 
              maxWidth: '1200px', 
              margin: '0 auto',
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Button
                variant="outlined"
                onClick={prevStep}
                sx={{
                  borderColor: '#0F2A52',
                  color: '#0F2A52',
                  '&:hover': {
                    borderColor: '#0A1F3F',
                    backgroundColor: 'rgba(15, 42, 82, 0.04)',
                  },
                }}
              >
                Previous
              </Button>
              
              <Button
                variant="contained"
                onClick={async () => {
                  // Get edited data from ComparePreview and proceed to confirm
                  if (comparePreviewRef.current) {
                    const editedData = comparePreviewRef.current.getEditedData();
                    await handleImportEditedData(editedData);
                  } else {
                    // For non-ZIP files, proceed directly
                    handleImportClick();
                  }
                }}
                disabled={importLoading || loading}
                sx={{ 
                  minWidth: 120,
                  backgroundColor: '#0F2A52',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#0A1F3F',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(15, 42, 82, 0.3)',
                    color: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                {importLoading ? 'Processing...' : 'Continue to Confirm'}
              </Button>
            </Box>
          </Box>
        </Grid>
      )}

      {/* Step 3: Confirm Import */}
      {currentStep === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card 
              elevation={0}
              sx={{
                borderRadius: 1.5,
                boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
                border: '1px solid rgba(15, 40, 76, 0.08)',
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{
                    color: '#0F2A52',
                    fontWeight: 600,
                    fontSize: '1rem',
                    mb: 2
                  }}
                >
                  Confirm Import
                </Typography>
                
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 3,
                    fontSize: '0.85rem',
                    lineHeight: 1.5
                  }}
                >
                  Review the data that will be imported and confirm to proceed.
                </Typography>

                {/* Import Confirmation Content - Inline instead of modal */}
                {pendingImportData && pendingImportData.length > 0 ? (
                  <Box>
                    {/* Summary */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ color: '#0F2A52', fontWeight: 600, mb: 1 }}>
                        Import Summary
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        <strong>{pendingImportData.length} records</strong> will be uploaded to your {config.entityType} data.
                      </Typography>
                    </Box>

                    {/* Data Preview with GitHub-style diff */}
                    <Box sx={{ 
                      maxHeight: 600, 
                      overflow: 'auto',
                      backgroundColor: '#ffffff',
                      border: '1px solid #d0d7de',
                      borderRadius: '6px',
                      p: 0
                    }}>
                      {/* GitHub-style header */}
                      <Box sx={{ 
                        backgroundColor: '#f6f8fa',
                        padding: '12px 16px',
                        borderBottom: '1px solid #d0d7de',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderTopLeftRadius: '6px',
                        borderTopRightRadius: '6px'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ 
                            width: '16px',
                            height: '16px',
                            backgroundColor: '#0F2A52',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Typography sx={{ color: 'white', fontSize: '0.7rem', fontWeight: 'bold' }}>
                              D
                            </Typography>
                          </Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#24292e', 
                              fontWeight: 600,
                              fontSize: '0.875rem'
                            }}
                          >
                            Data Preview with Changes
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          fontSize: '0.75rem',
                          color: '#656d76',
                          backgroundColor: '#ffffff',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          border: '1px solid #d0d7de'
                        }}>
                          {pendingImportData.length} records
                        </Box>
                      </Box>
                      
                      {/* Diff content */}
                      <Box sx={{ p: 0 }}>
                        {pendingImportData.map((item, index) => (
                          <JsonDiffViewer
                            key={index}
                            currentData={item.current}
                            incomingData={item.incoming}
                            recordIndex={index}
                            recordType="Record"
                            editable={false}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ 
                    p: 3, 
                    textAlign: 'center', 
                    backgroundColor: 'rgba(15, 42, 82, 0.04)',
                    borderRadius: 1.5,
                    border: '1px solid rgba(15, 42, 82, 0.08)'
                  }}>
                    <Typography variant="body1" color="text.secondary">
                      No data available to import.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

      {/* Fixed Bottom Navigation Buttons */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: 'white',
          borderTop: '1px solid #e0e0e0',
          p: 2,
          zIndex: 1000,
          boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box sx={{ 
          maxWidth: '1200px', 
          margin: '0 auto',
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Button
            variant="outlined"
            onClick={prevStep}
            sx={{
              borderColor: '#0F2A52',
              color: '#0F2A52',
              '&:hover': {
                borderColor: '#0A1F3F',
                backgroundColor: 'rgba(15, 42, 82, 0.04)',
              },
            }}
          >
            Previous
          </Button>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setCurrentStep(0);
                setCompletedSteps([]);
                resetAllStates();
              }}
              sx={{
                borderColor: '#d32f2f',
                color: '#d32f2f',
                '&:hover': {
                  borderColor: '#b71c1c',
                  backgroundColor: 'rgba(211, 47, 47, 0.04)',
                },
              }}
            >
              Cancel
            </Button>
            
            <Button
              variant="contained"
              onClick={() => setConfirmationModalOpen(true)}
              disabled={loading || pendingImportData.length === 0}
              startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
              sx={{
                backgroundColor: '#0F2A52',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#0A1F3F',
                },
                '&:disabled': {
                  backgroundColor: 'rgba(15, 42, 82, 0.3)',
                  color: 'rgba(255, 255, 255, 0.5)',
                },
              }}
            >
              {loading ? 'Importing...' : 
               pendingImportData.length === 0 ? 'No Data to Import' : 
               `Import ${pendingImportData.length} Records`}
            </Button>
          </Box>
        </Box>
      </Box>
        </Grid>
      )}

      {/* Confirmation Modal */}
      {confirmationModalOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 2,
              p: 3,
              maxWidth: 500,
              width: '90%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ color: '#0F2A52', fontWeight: 600 }}>
              Confirm Import
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Are you sure you want to import <strong>{pendingImportData.length} records</strong> to your {selectedEntity?.entity || config.entityType} data? 
              This action cannot be undone.
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setConfirmationModalOpen(false)}
                sx={{
                  borderColor: '#0F2A52',
                  color: '#0F2A52',
                  '&:hover': {
                    borderColor: '#0A1F3F',
                    backgroundColor: 'rgba(15, 42, 82, 0.04)',
                  },
                }}
              >
                Cancel
              </Button>
              
              <Button
                variant="contained"
                onClick={performImport}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
                sx={{
                  backgroundColor: '#0F2A52',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#0A1F3F',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(15, 42, 82, 0.3)',
                    color: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                {loading ? 'Importing...' : 'Confirm Import'}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
});

export default ImportSection;
